import { faCalendarDays } from '@fortawesome/pro-regular-svg-icons';
import useNonInitialEffect from '@versiondos/hooks';
import {
    cashoutThunk,
    fetchPayoutsThunk,
    getCashoutDataThunk,
    getNextPayoutThunk,
    PaymentsStatus
} from '@spike/payments-action';
import { PageResult, Period } from '@spike/model';
import { Cashout, PaymentsNextPayout, PaymentsPayout } from '@spike/payments-model';
import { showError } from '@spike/notifications-action';
import clsx from 'clsx';
import { CalculatingSpinner, CalendarFilter } from 'components/UI';
import ExportButton from 'components/UI/ExportCSV/ExportButton';
import { Spinner } from 'components/UI/V3/Spinner/Spinner';
import { Table } from 'components/UI/V3/Table/Table';
import { TableBody } from 'components/UI/V3/Table/Body/TableBody';
import { TableCell } from 'components/UI/V3/Table/Cell/TableCell';
import { TableHeader } from 'components/UI/V3/Table/Header/TableHeader';
import { TableHeaderCell } from 'components/UI/V3/Table/HeaderCell/TableHeaderCell';
import { TableRow } from 'components/UI/V3/Table/Row/TableRow';
import { FunctionComponent, useEffect, useRef, useState } from 'react';
import moment from 'moment-timezone';
import { CSVLink } from 'react-csv';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { RootState } from 'store';
import AvailableBalance from './AvailableBalance';
import PayoutModal from './PayoutModal';
import PayoutProcessingBanner from './PayoutProcessingBanner';
import ScheduledPayout from './ScheduledPayout';
import { useApiClientWrapper, useMarketplace } from 'hooks';
import { formatPayoutAmount, isPayoutNowMarketplaceEnabled } from 'utils/PaymentsUtils';
import styles from './Transfers.module.scss';

interface TransfersProps {
    className?: string;
    onClose?: () => void;
}

const PAYOUTS_PAGE_SIZE = 10;
const PAYOUTS_EXPORT_PAGE_SIZE = 99999;

const PAYOUT_EXPORT_HEADERS = ['Transfer Date', 'Status', 'Amount'];

interface PayoutExportDto {
    date: string | null;
    creation_date: string | null;
    status: string | null;
    value: string | null;
    amount: string | null;
}

const toPercentage = (rate: string | number): number => {
    const value = Number(rate || 0);
    return value <= 1 ? Number((value * 100).toFixed(2)) : value;
};

export const Transfers: FunctionComponent<TransfersProps> = props => {
    const dispatch = useDispatch();
    const store = useStore<RootState>();
    const apiClientWrapper = useApiClientWrapper();
    const marketplace = useMarketplace();
    const showPayoutNowFeatures = isPayoutNowMarketplaceEnabled(marketplace.id);

    const payouts = useSelector<RootState, PageResult<PaymentsPayout>>(state => state.payments.payouts);
    const paymentsStatus = useSelector<RootState, PaymentsStatus>(state => state.payments.status);
    const nextPayout = useSelector<RootState, PaymentsNextPayout | undefined>(state => state.payments.nextPayout);
    const cashoutData = useSelector<RootState, Cashout | undefined>(state => state.payments.cashoutData);

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(PAYOUTS_PAGE_SIZE);
    const [allTime, setAllTime] = useState(true);
    const [calendarSelection, setCalendarSelection] = useState<string | undefined>('All time');
    const [period, setPeriod] = useState<Period>({
        from: moment().startOf('day'),
        to: moment().endOf('day')
    });
    const [loading, setLoading] = useState(true);
    const [balancesLoading, setBalancesLoading] = useState(true);
    const [isPayoutProcessing, setIsPayoutProcessing] = useState(false);
    const [showPayoutModal, setShowPayoutModal] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [exportRecords, setExportRecords] = useState<Array<Array<string>>>([]);
    const [exportReady, setExportReady] = useState(false);
    const csvLinkRef = useRef<HTMLAnchorElement | null>(null);
    const refreshTimeoutRef = useRef<number>();

    useEffect(() => {
        setLoading(true);
        dispatch(
            fetchPayoutsThunk(
                apiClientWrapper,
                allTime ? null : period.from,
                allTime ? null : period.to,
                page,
                pageSize
            )
        );
    }, [page, pageSize, allTime, period]);

    useEffect(() => {
        if (exportReady) {
            csvLinkRef.current?.click();
            setExportReady(false);
        }
    }, [exportReady]);

    useEffect(() => {
        const loadBalances = async () => {
            setBalancesLoading(true);
            await Promise.all([
                dispatch(getNextPayoutThunk(apiClientWrapper)),
                dispatch(getCashoutDataThunk(apiClientWrapper))
            ]);
            setBalancesLoading(false);
        };

        loadBalances();
    }, []);

    useEffect(() => {
        return () => {
            if (refreshTimeoutRef.current) {
                window.clearTimeout(refreshTimeoutRef.current);
            }
        };
    }, []);

    useNonInitialEffect(() => {
        if (PaymentsStatus.FetchPayoutsSuccess === paymentsStatus) {
            setLoading(false);
        } else if (PaymentsStatus.Error === paymentsStatus) {
            setLoading(false);
        }
    }, [paymentsStatus]);

    const handlePayoutSuccess = () => {
        setIsPayoutProcessing(true);

        if (refreshTimeoutRef.current) {
            window.clearTimeout(refreshTimeoutRef.current);
        }

        refreshTimeoutRef.current = window.setTimeout(async () => {
            await Promise.all([
                dispatch(getCashoutDataThunk(apiClientWrapper)),
                dispatch(getNextPayoutThunk(apiClientWrapper)),
                dispatch(
                    fetchPayoutsThunk(
                        apiClientWrapper,
                        allTime ? null : period.from,
                        allTime ? null : period.to,
                        page,
                        pageSize
                    )
                )
            ]);
            setIsPayoutProcessing(false);
        }, 15000);
    };

    const handleConfirmPayout = async () => {
        const cashoutAmount = Number(cashoutData?.cashout.amount ?? 0);
        await dispatch(cashoutThunk(apiClientWrapper, cashoutAmount));

        const status = store.getState().payments.status;
        setShowPayoutModal(false);

        if (status === PaymentsStatus.CashoutSuccess) {
            handlePayoutSuccess();
        }
    };

    const changePageHandler = (nextPage: number) => {
        setPage(nextPage);
    };

    const changePageSizeHandler = (size: number) => {
        setPage(1);
        setPageSize(size);
    };

    const changePeriodHandler = (nextPeriod: Period, isAllTime: boolean, optionSelected?: string) => {
        setPage(1);
        setAllTime(isAllTime);
        setCalendarSelection(isAllTime ? 'All time' : optionSelected);
        setPeriod({
            from: nextPeriod.from.clone(),
            to: nextPeriod.to.clone()
        });
    };

    const exportHandler = () => {
        setExporting(true);
        setExportRecords([]);
        setExportReady(false);

        const fromQuery = !allTime && period.from ? `&from=${period.from.format('YYYY-MM-DD')}` : '';
        const toQuery = !allTime && period.to ? `&to=${period.to.format('YYYY-MM-DD')}` : '';
        const url = `marketplace/payouts_list?marketplace_id=${apiClientWrapper.marketplaceId}${fromQuery}${toQuery}&page=1&size=${PAYOUTS_EXPORT_PAGE_SIZE}`;

        apiClientWrapper.apiClient
            .get<{ payouts: Array<PayoutExportDto> }>(url, apiClientWrapper.requestConfig)
            .then(response => {
                setExportRecords(
                    (response.data.payouts ?? []).map(payout => {
                        const date = payout.date || payout.creation_date;
                        const amount = payout.value || payout.amount;

                        return [
                            date ? moment(date).format('MMM DD, YYYY, hh:mm a') : '',
                            (payout.status || '-').replace(/\b\w/g, letter => letter.toUpperCase()),
                            amount ? formatPayoutAmount(amount) : ''
                        ];
                    })
                );
                setExporting(false);
                setExportReady(true);
            })
            .catch(() => {
                setExporting(false);
                dispatch(showError('Error exporting payouts.'));
            });
    };

    const setCsvLinkRef = (instance: CSVLink | HTMLAnchorElement | null) => {
        csvLinkRef.current =
            instance instanceof HTMLAnchorElement
                ? instance
                : (instance as { link?: HTMLAnchorElement } | null)?.link ?? null;
    };

    const pagination = {
        page,
        totalPages: Math.max(payouts.pagination?.totalPages ?? 1, 1),
        size: pageSize,
        isLoading: false
    };

    const hasNoPayoutsForSelectedDate =
        !loading && !allTime && (payouts.pagination?.totalRecords ?? payouts.page.length) === 0;

    const accountNumber = nextPayout?.bankAccountNumber || nextPayout?.bankAccount?.accountNumber;
    const scheduledAmount = cashoutData?.balances.available || 0;
    const availableAmount =
        Number(cashoutData?.balances.available || 0) + Number(cashoutData?.balances.pendingAvailable || 0);
    const payoutAmount = Number(cashoutData?.cashout.amount ?? nextPayout?.amount ?? 0);
    const amountFee = Number(cashoutData?.cashout.fee.amount ?? 0);
    const percentageFee = toPercentage(cashoutData?.cashout.fee.rate ?? 0);
    const total = Number(cashoutData?.cashout.netAmount ?? payoutAmount - amountFee);

    return (
        <div className={clsx(styles.container, props.className)}>
            {isPayoutProcessing && <PayoutProcessingBanner />}

            <div className={styles.header}>
                <h1 className={styles.title}>Payouts</h1>
                <p className={styles.description}>
                    At the end of each business day, we’ll automatically transfer your Scheduled Payout to your linked
                    bank account. Funds typically arrive within 2 business days. You can also use Instant Payout to
                    transfer your Available Balance anytime.
                </p>
                {(balancesLoading || accountNumber) && (
                    <p className={styles.account}>
                        <span>Linked Account</span>
                        {balancesLoading ? (
                            <CalculatingSpinner className={styles.accountSpinner} />
                        ) : (
                            <span>{`••••••${accountNumber?.slice(-4)}`}</span>
                        )}
                    </p>
                )}
                <div className={styles.summary}>
                    {showPayoutNowFeatures && (
                        <div className={styles.cards}>
                            <ScheduledPayout
                                amount={scheduledAmount}
                                date={nextPayout?.date}
                                loading={balancesLoading}
                            />
                            <AvailableBalance
                                amount={availableAmount}
                                loading={balancesLoading}
                                showPayoutNow={Boolean(cashoutData?.cashout.available) && !isPayoutProcessing}
                                onPayoutNow={() => setShowPayoutModal(true)}
                            />
                        </div>
                    )}
                    <div className={styles.toolbar}>
                        <CalendarFilter
                            hideUpcoming
                            hideTomorrow
                            hideNext7Days
                            optionsOnLeft
                            optionSelected={calendarSelection}
                            buttonIcon={faCalendarDays}
                            period={period}
                            className={styles.datePicker}
                            onChange={changePeriodHandler}
                        />
                        <CSVLink
                            ref={setCsvLinkRef}
                            headers={PAYOUT_EXPORT_HEADERS}
                            data={exportRecords}
                            filename={`${marketplace.basics.businessName} Payouts.csv`}
                            target="_blank"
                        />
                        <ExportButton
                            exporting={exporting}
                            disabled={hasNoPayoutsForSelectedDate}
                            onClick={exportHandler}
                        />
                    </div>
                </div>
            </div>

            <Table
                fullHeight
                className={styles.payoutTable}
                wrapperClassName={styles.tableWrapper}
                pagination={pagination}
                onPageChange={changePageHandler}
                onSizeChange={changePageSizeHandler}
            >
                <TableHeader>
                    <TableHeaderCell className={clsx(styles.noShowOnMobile, styles.firstHeaderCell)}>
                        Transfer Date
                    </TableHeaderCell>
                    <TableHeaderCell className={clsx(styles.noShowOnMobile, styles.hideElement)}>
                        Account No.
                    </TableHeaderCell>
                    {showPayoutNowFeatures && (
                        <TableHeaderCell className={styles.noShowOnMobile}>Status</TableHeaderCell>
                    )}
                    <TableHeaderCell className={styles.noShowOnMobile}>Amount</TableHeaderCell>
                </TableHeader>

                {loading ? (
                    <TableBody>
                        <tr>
                            <td
                                colSpan={showPayoutNowFeatures ? 4 : 3}
                                className={styles.loadingCell}
                            >
                                <div className={styles.loadingContent}>
                                    <Spinner size={36} />
                                </div>
                            </td>
                        </tr>
                    </TableBody>
                ) : hasNoPayoutsForSelectedDate ? (
                    <TableBody>
                        <tr>
                            <td
                                colSpan={showPayoutNowFeatures ? 4 : 3}
                                className={styles.loadingCell}
                            >
                                <div className={styles.loadingContent}>
                                    <p className={styles.emptyMessage}>No payouts were made.</p>
                                </div>
                            </td>
                        </tr>
                    </TableBody>
                ) : (
                    <TableBody>
                        {payouts.page.map((payout, index) => (
                            <TableRow key={index}>
                                <TableCell className={styles.dateCell}>
                                    {payout.date && payout.date.format('MMM DD, YYYY, hh:mm a')}
                                    {payout.accountNumber && (
                                        <div className={clsx(styles.noShowOnDesktop, styles.payoutAccountNumber)}>
                                            {`••••••• ${payout.accountNumber.slice(-4)}`}
                                        </div>
                                    )}
                                </TableCell>
                                <TableCell className={clsx(styles.noShowOnMobile, styles.hideElement)}>
                                    {payout.accountNumber && `••••••••${payout.accountNumber.slice(-4)}`}
                                </TableCell>
                                {showPayoutNowFeatures && (
                                    <TableCell className={clsx(styles.noShowOnMobile, styles.payoutStatus)}>
                                        {payout.status || '-'}
                                    </TableCell>
                                )}
                                <TableCell className={styles.payoutAmount}>
                                    {payout.amount && formatPayoutAmount(payout.amount)}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                )}
            </Table>

            {showPayoutModal && (
                <PayoutModal
                    amount={payoutAmount}
                    amountFee={amountFee}
                    percentageFee={percentageFee}
                    total={total}
                    onClose={() => setShowPayoutModal(false)}
                    onConfirm={handleConfirmPayout}
                />
            )}
        </div>
    );
};

export default Transfers;
